import { useCallback, useEffect, useState } from 'react'
import { pb, isAbort, isRejection } from './pb'
import type { CovellaUser } from './session'

export interface SessionState {
  user: CovellaUser | null
  /** True until the first refresh against the server has settled. */
  loading: boolean
  /**
   * Set when the last refresh could not reach the server. The session is still shown, because
   * signing her out over a flaky connection would be worse, but membership state may be stale.
   */
  offline: boolean
}

/**
 * The signed-in user, re-checked against the server on load and whenever the tab is refocused.
 *
 * Refreshing matters more here than in the app. `covella_member` and `covella_member_until`
 * only ever change via the Stripe webhook or an admin, so a tab left open across a renewal
 * would otherwise sit on a stale answer indefinitely. The app gets this for free on resume;
 * a browser tab has no resume, so focus stands in for it.
 *
 * A failed refresh signs her out **only** when the server actually refused the token. Anything
 * else, a dropped connection, a server restart, leaves the session in place. Getting this
 * backwards is what makes a portal look empty for no reason.
 */
export function useSession(): SessionState & {
  refresh: () => Promise<void>
  signOut: () => void
} {
  const [user, setUser] = useState<CovellaUser | null>(
    (pb.authStore.record as CovellaUser | null) ?? null,
  )
  const [loading, setLoading] = useState(pb.authStore.isValid)
  const [offline, setOffline] = useState(false)

  // The store is the source of truth: login, sign-out and token refresh all go through it,
  // so subscribing here means no caller has to remember to update React state as well.
  useEffect(() => {
    return pb.authStore.onChange(() => {
      setUser((pb.authStore.record as CovellaUser | null) ?? null)
    })
  }, [])

  const refresh = useCallback(async () => {
    if (!pb.authStore.isValid) {
      setLoading(false)
      return
    }
    try {
      await pb.collection('users').authRefresh()
      setOffline(false)
    } catch (error) {
      // A cancelled refresh says nothing about the network. Marking her offline for it put a
      // staleness warning on a session that was fine.
      if (isAbort(error)) return
      if (isRejection(error)) {
        pb.authStore.clear()
      } else {
        setOffline(true)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const onFocus = () => void refresh()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refresh])

  const signOut = useCallback(() => {
    pb.authStore.clear()
  }, [])

  return { user, loading, offline, refresh, signOut }
}
